import React, { useEffect, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { X, FileText, Send, Save, Loader2, ExternalLink } from "lucide-react";

/**
 * Western Colloid Warranty Application modal.
 *
 * Autofills from the CRM (contractor identity, contact/owner, property,
 * cost_items, warranty tier, final-inspection date), lets the rep edit
 * blanks, and then Preview → Save → optionally Email direct to
 * scwarranty@westerncolloid.com.
 *
 * Fields mirror the AcroForm names in `AppWnty-WCSC_2020-01.pdf` so we
 * can round-trip them straight into the PDF template server-side.
 */
const FIELD_GROUPS = [
  {
    title: "Application",
    rows: [
      [{ k: "Date", label: "Date", type: "date" }, { k: "Years", label: "Warranty Years", placeholder: "5, 10, 15, 20…" }],
      [
        { k: "Check Box2", label: "Standard", type: "checkbox" },
        { k: "Check Box1", label: "Material & Labor", type: "checkbox" },
      ],
    ],
  },
  {
    title: "Contractor",
    rows: [
      [{ k: "Applied By Contractor Name", label: "Contractor Name", full: true }],
      [{ k: "Contractor Address", label: "Address (line 1)" }, { k: "Contractor Address2", label: "City / State / Zip" }],
      [{ k: "Contractor Contact", label: "Contact" }, { k: "Contractor Phone", label: "Phone" }],
      [{ k: "eMail", label: "Contractor Email" }, { k: "Additional Contact FieldEstimator", label: "Estimator / Additional Contact" }],
    ],
  },
  {
    title: "Building / Location",
    rows: [
      [{ k: "Name of Building or Building Use or Type", label: "Building Name / Use", full: true }],
      [{ k: "Building Location Address 1", label: "Building Address (line 1)" }, { k: "Building Location Address 2", label: "City / State / Zip" }],
      [{ k: "Building Location Address 3", label: "Location (line 3, optional)", full: true }],
      [{ k: "Location Notes description", label: "Location Notes", type: "textarea", full: true }],
    ],
  },
  {
    title: "Owner / Property Manager",
    rows: [
      [{ k: "Name Of Owner", label: "Owner Name" }, { k: "Property Manager", label: "Property Manager" }],
      [{ k: "OwnersPM Address", label: "Owner/PM Address (line 1)" }, { k: "OwnersPM Address2", label: "City / State / Zip" }],
      [{ k: "Owner Phone", label: "Owner Phone" }, { k: "eMail_2", label: "Owner Email" }],
      [{ k: "Contact", label: "On-site Contact", full: true }],
    ],
  },
  {
    title: "System / Materials",
    rows: [
      [{ k: "Systems Specification No", label: "System Spec No." }, { k: "Size-Squares", label: "Size (Full Squares)" }],
      [{ k: "List Western Colloid Products and Quantities Used 1", label: "Products / Quantities (line 1)", full: true }],
      [{ k: "List Western Colloid Products and Quantities Used 2", label: "Products / Quantities (line 2)", full: true }],
      [{ k: "List Western Colloid Products and Quantities Used 3", label: "Products / Quantities (line 3)", full: true }],
      [{ k: "Date of Completion", label: "Date of Completion", type: "date" }, { k: "Consultant or Specifier if Applicable", label: "Consultant / Specifier" }],
    ],
  },
  {
    title: "Notes",
    rows: [
      [{ k: "Notes 1", label: "Notes (line 1)", full: true }],
      [{ k: "Notes 2", label: "Notes (line 2)", full: true }],
      [{ k: "Notes 3", label: "Notes (line 3)", full: true }],
    ],
  },
];

function Field({ field, value, onChange }) {
  const common = "w-full border border-zinc-300 rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:border-zinc-950";
  const tid = `wcsc-field-${field.k.replace(/[^a-z0-9]+/gi, "-")}`;
  if (field.type === "checkbox") {
    return (
      <label className="flex items-center gap-2 text-sm text-zinc-800 cursor-pointer" data-testid={`${tid}-wrap`}>
        <input
          type="checkbox"
          className="h-4 w-4"
          checked={!!value}
          onChange={(e) => onChange(field.k, e.target.checked)}
          data-testid={tid}
        />
        {field.label}
      </label>
    );
  }
  if (field.type === "textarea") {
    return (
      <label className="block">
        <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-zinc-600 mb-1">{field.label}</div>
        <textarea
          className={`${common} min-h-[60px]`}
          value={value || ""}
          onChange={(e) => onChange(field.k, e.target.value)}
          data-testid={tid}
        />
      </label>
    );
  }
  return (
    <label className="block">
      <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-zinc-600 mb-1">{field.label}</div>
      <input
        type={field.type || "text"}
        className={common}
        value={value || ""}
        onChange={(e) => onChange(field.k, e.target.value)}
        placeholder={field.placeholder || ""}
        data-testid={tid}
      />
    </label>
  );
}

export default function WcscWarrantyModal({ dealId, open, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [values, setValues] = useState({});
  const [recipient, setRecipient] = useState("scwarranty@westerncolloid.com");
  const [ccMe, setCcMe] = useState(true);
  const [dealTitle, setDealTitle] = useState("");
  const [savedFileId, setSavedFileId] = useState(null);
  const [senderEmail, setSenderEmail] = useState("projects@sealtechsolutions.co");
  const [finalInvoice, setFinalInvoice] = useState(null);
  const [includeInvoice, setIncludeInvoice] = useState(false);

  useEffect(() => {
    if (!open || !dealId) return;
    setLoading(true);
    setSavedFileId(null);
    api
      .get(`/deals/${dealId}/wcsc-warranty/defaults`)
      .then((r) => {
        setValues(r.data?.values || {});
        setRecipient(r.data?.recipient_default || "scwarranty@westerncolloid.com");
        setSenderEmail(r.data?.sender_email || "projects@sealtechsolutions.co");
        setDealTitle(r.data?.deal_title || "");
        setFinalInvoice(r.data?.final_invoice || null);
        // Pre-toggle the "Include Final Invoice" checkbox when we detect one.
        setIncludeInvoice(!!r.data?.final_invoice);
      })
      .catch((err) => {
        toast.error(formatApiError(err?.response?.data?.detail) || "Failed to load defaults");
      })
      .finally(() => setLoading(false));
  }, [open, dealId]);

  const update = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const r = await api.post(`/deals/${dealId}/wcsc-warranty/preview`, { values }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/deals/${dealId}/wcsc-warranty/generate`, { values });
      setSavedFileId(r.data?.file?.id || null);
      toast.success("Warranty application saved to Deal Documents");
      onSaved?.(r.data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndEmail = async () => {
    setEmailing(true);
    try {
      const payload = {
        values,
        to_email: recipient,
        include_final_invoice: includeInvoice,
        ...(savedFileId ? { file_id: savedFileId } : {}),
      };
      if (!ccMe) payload.cc_email = "";
      const r = await api.post(`/deals/${dealId}/wcsc-warranty/email`, payload);
      toast.success(r.data?.message || "Warranty emailed");
      setSavedFileId(r.data?.file_id || savedFileId);
      onSaved?.(r.data);
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Email failed");
    } finally {
      setEmailing(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-8 px-4" data-testid="wcsc-warranty-modal">
      <div className="bg-white rounded-sm border-2 border-zinc-950 w-full max-w-4xl shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-zinc-950 text-white px-6 py-4 flex items-center justify-between border-b-2 border-zinc-950 z-10">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400">Western Colloid</div>
            <h2 className="font-heading text-xl font-bold tracking-tight">Warranty Application</h2>
            {dealTitle && <div className="text-xs text-zinc-400 mt-0.5">{dealTitle}</div>}
          </div>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-white p-1"
            data-testid="wcsc-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-12 flex items-center justify-center text-zinc-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading defaults from deal…
          </div>
        ) : (
          <>
            <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              {FIELD_GROUPS.map((group) => (
                <section key={group.title}>
                  <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-blue-700 mb-3 border-b border-zinc-200 pb-1">
                    {group.title}
                  </div>
                  <div className="space-y-3">
                    {group.rows.map((row, i) => (
                      <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {row.map((f) => (
                          <div key={f.k} className={f.full ? "md:col-span-2" : ""}>
                            <Field field={f} value={values[f.k]} onChange={update} />
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </section>
              ))}

              {/* Send-to config */}
              <section className="border-t-2 border-zinc-950 pt-4 mt-6">
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-blue-700 mb-3">Submit To Western Colloid</div>
                <div className="text-[11px] text-zinc-600 mb-3 bg-zinc-50 border border-zinc-200 rounded-sm px-3 py-2" data-testid="wcsc-sender-hint">
                  Sending from <span className="font-semibold text-zinc-900">{senderEmail}</span> via Gmail.
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="block">
                    <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-zinc-600 mb-1">Recipient Email</div>
                    <input
                      type="email"
                      className="w-full border border-zinc-300 rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:border-zinc-950"
                      value={recipient}
                      onChange={(e) => setRecipient(e.target.value)}
                      data-testid="wcsc-recipient-email"
                    />
                  </label>
                  <label className="flex items-end gap-2 text-sm text-zinc-800 cursor-pointer mb-1.5">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={ccMe}
                      onChange={(e) => setCcMe(e.target.checked)}
                      data-testid="wcsc-cc-me"
                    />
                    CC me on the submission
                  </label>
                </div>
                <label
                  className={`mt-3 flex items-start gap-2 text-sm text-zinc-800 cursor-pointer border rounded-sm px-3 py-2 ${
                    finalInvoice ? "border-emerald-200 bg-emerald-50/40" : "border-zinc-200 bg-zinc-50"
                  }`}
                  data-testid="wcsc-include-invoice-wrap"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 mt-0.5"
                    checked={includeInvoice}
                    onChange={(e) => setIncludeInvoice(e.target.checked)}
                    disabled={!finalInvoice}
                    data-testid="wcsc-include-invoice"
                  />
                  <div className="text-[12px] leading-snug">
                    <div className="font-semibold text-zinc-900">
                      Also attach Final Invoice
                      {finalInvoice?.invoice_number ? ` — #${finalInvoice.invoice_number}` : ""}
                    </div>
                    <div className="text-zinc-600">
                      {finalInvoice
                        ? "Sends the warranty application AND the final invoice PDF in one email."
                        : "This deal has no invoice yet — run Mark Complete first to draft one, then re-open this modal."}
                    </div>
                  </div>
                </label>
              </section>
            </div>

            {/* Footer actions */}
            <div className="sticky bottom-0 bg-zinc-50 border-t-2 border-zinc-950 px-6 py-4 flex flex-wrap items-center justify-end gap-2">
              <button
                onClick={onClose}
                className="px-3 py-2 text-sm text-zinc-700 hover:text-zinc-950"
                data-testid="wcsc-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handlePreview}
                disabled={previewing}
                className="px-3 py-2 text-sm border border-zinc-300 rounded-sm hover:border-zinc-950 flex items-center gap-1.5 disabled:opacity-50"
                data-testid="wcsc-preview-btn"
              >
                {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                Preview PDF
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-3 py-2 text-sm border border-zinc-950 rounded-sm bg-white hover:bg-zinc-50 flex items-center gap-1.5 disabled:opacity-50"
                data-testid="wcsc-save-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save to Deal
              </button>
              <button
                onClick={handleSaveAndEmail}
                disabled={emailing || !recipient}
                className="px-3 py-2 text-sm rounded-sm bg-zinc-950 text-white hover:bg-black flex items-center gap-1.5 disabled:opacity-50"
                data-testid="wcsc-save-email-btn"
              >
                {emailing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Save & Email to Western Colloid
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
